import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestNotificationsRefresh } from '../notifications/useStoredNotifications';
import { ConfirmCard } from '../../shared/components/ConfirmCard';
import { sandboxTransmissionActionsEnabled } from '../../shared/commercialActions';
import { checkBatchGateway as defaultGateway, type CheckBatchGateway } from './index';
import type { ApprovedPayRun, CheckBatch, CheckBatchLine } from './types';
import { describeFailure } from './errors';
import { plural } from './format';
import { AckDialog } from './components/AckDialog';
import { ApprovedStep } from './components/ApprovedStep';
import { CheckTable, type CheckNumberEdit } from './components/CheckTable';
import { DraftDialog } from './components/DraftDialog';
import { FilterBar } from './components/FilterBar';
import { ReadinessStep } from './components/ReadinessStep';
import { ResultDialog } from './components/ResultDialog';
import { ReviewStep } from './components/ReviewStep';
import { Stepper } from './components/Stepper';
import { SummaryBar } from './components/SummaryBar';
import { CheckDeliveryNote, TrialBanner } from './components/TrialBanner';
import './check-batch.css';

type Step = 1 | 2 | 3 | 4;
type Dialog =
  | { kind: 'remove'; lineIds: string[]; label: string }
  | { kind: 'removed' }
  | { kind: 'ack' }
  | { kind: 'draftSaved' }
  | { kind: 'discard' };

interface View {
  key: string;
  batch: CheckBatch | null;
  step: Step;
  /** A draft the admin explicitly saved earlier was found on entry: ask what to do with it. */
  existingDraft: boolean;
  error: string | null;
}

type RunsLoad = { kind: 'loading' } | { kind: 'loaded'; runs: ApprovedPayRun[] } | { kind: 'error'; message: string };

function newKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `k-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

function asStep(n: number): Step {
  return n >= 3 ? 3 : n <= 1 ? 1 : 2;
}

/**
 * EPN-231 — the Write Check flow: Pay Employees → Readiness Check → Review Payslip → Approved.
 *
 * The server owns every rule (eligibility, totals, check-number validity, readiness, approval); this component asks and shows. Approving only
 * records an approval: it prints, mails and pays nothing, and the approve action is not offered at all in a production bundle (EPN-98).
 */
export function WriteCheckFlow({ tabs, gateway = defaultGateway }: { tabs: ReactNode; gateway?: CheckBatchGateway }) {
  const navigate = useNavigate();

  // ---- pay runs -----------------------------------------------------------------------------------------------------------------------------
  const [runsNonce, setRunsNonce] = useState(0);
  const [runs, setRuns] = useState<RunsLoad & { nonce: number }>({ kind: 'loading', nonce: -1 });
  const [runId, setRunId] = useState<string | null>(null);
  const defaultRunApplied = useRef(false);

  useEffect(() => {
    let cancelled = false;
    gateway
      .listApprovedRuns()
      .then((list) => {
        if (cancelled) return;
        setRuns({ kind: 'loaded', runs: list, nonce: runsNonce });
        // The newest approved run is selected the first time only; "Clear Filter" must stay cleared on a later refresh.
        if (!defaultRunApplied.current) {
          defaultRunApplied.current = true;
          setRunId(list[0]?.id ?? null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setRuns({ kind: 'error', message: describeFailure(err, 'The approved payroll runs couldn’t be loaded. Try again.').message, nonce: runsNonce });
      });
    return () => {
      cancelled = true;
    };
  }, [gateway, runsNonce]);
  const runsState: RunsLoad = runs.nonce === runsNonce ? runs : { kind: 'loading' };

  // ---- the batch for the selected run --------------------------------------------------------------------------------------------------------
  const [batchNonce, setBatchNonce] = useState(0);
  const [view, setView] = useState<View | null>(null);
  const key = runId ? `${runId}#${batchNonce}` : null;
  const loading = key !== null && view?.key !== key;

  useEffect(() => {
    if (!runId || key === null) return;
    let cancelled = false;
    (async () => {
      const existing = await gateway.listBatches(runId);
      const approved = existing.find((b) => b.status === 'Approved');
      if (approved) return { batch: approved, step: 4 as Step, existingDraft: false };
      const draft = existing.find((b) => b.status === 'Draft');
      if (draft) return { batch: draft, step: 1 as Step, existingDraft: draft.lastSavedAtUtc !== null };
      return { batch: await gateway.createBatch(runId), step: 1 as Step, existingDraft: false };
    })()
      .then((r) => {
        if (!cancelled) setView({ key, batch: r.batch, step: r.step, existingDraft: r.existingDraft, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) setView({ key, batch: null, step: 1, existingDraft: false, error: describeFailure(err, 'The payment batch couldn’t be loaded. Try again.').message });
      });
    return () => {
      cancelled = true;
    };
  }, [gateway, runId, key]);

  // With no pay run selected there is no batch to show, whatever was loaded before ("Clear Filter", "Back to Pay Employees").
  const batch = runId === null || loading ? null : (view?.batch ?? null);
  const step: Step = batch ? (view?.step ?? 1) : 1;

  // ---- local UI state -------------------------------------------------------------------------------------------------------------------------
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [edit, setEdit] = useState<CheckNumberEdit | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmedVersion, setConfirmedVersion] = useState<number | null>(null);
  const approveKey = useRef<{ version: number; key: string } | null>(null);

  const setBatch = useCallback((next: CheckBatch, nextStep?: Step) => {
    setView((v) => (v ? { ...v, batch: next, step: nextStep ?? v.step } : v));
  }, []);

  /** Runs one server action; a refusal is shown in plain language, and a version conflict reloads the latest batch. */
  const act = useCallback(
    async (work: () => Promise<void>, fallback: string): Promise<boolean> => {
      setBusy(true);
      setActionError(null);
      setNotice(null);
      try {
        await work();
        return true;
      } catch (err) {
        const failure = describeFailure(err, fallback);
        setActionError(failure.message);
        if (failure.versionConflict && batch) {
          try {
            setBatch(await gateway.getBatch(batch.id));
          } catch {
            /* the message above already tells the admin to retry */
          }
        }
        return false;
      } finally {
        setBusy(false);
      }
    },
    [batch, gateway, setBatch],
  );

  const lines = useMemo(() => (batch ? batch.lines.filter((l) => l.included) : []), [batch]);
  const selectedIncluded = useMemo(() => lines.filter((l) => selected.has(l.id)), [lines, selected]);
  const confirmed = batch !== null && confirmedVersion === batch.version;

  // ---- handlers ---------------------------------------------------------------------------------------------------------------------------------
  function selectRun(id: string | null) {
    setRunId(id);
    setSelected(new Set());
    setEdit(null);
    setNotice(null);
    setActionError(null);
    setConfirmedVersion(null);
  }

  function toggle(lineId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) => (lines.length > 0 && lines.every((l) => prev.has(l.id)) ? new Set() : new Set(lines.map((l) => l.id))));
  }

  function startEdit(line: CheckBatchLine) {
    setActionError(null);
    setEdit({ lineId: line.id, value: line.checkNumber, error: null, saving: false });
  }

  async function commitEdit() {
    if (!batch || !edit) return;
    const value = edit.value.trim();
    if (value === '') {
      setEdit({ ...edit, error: 'Enter a check number.' });
      return;
    }
    const line = batch.lines.find((l) => l.id === edit.lineId);
    if (line && line.checkNumber === value) {
      setEdit(null);
      return;
    }
    setEdit({ ...edit, saving: true, error: null });
    try {
      const next = await gateway.editCheckNumber(batch.id, edit.lineId, value, batch.version);
      setBatch(next);
      setEdit(null);
    } catch (err) {
      const failure = describeFailure(err, 'The check number couldn’t be saved. Try again.');
      if (failure.versionConflict) {
        setEdit(null);
        setActionError(failure.message);
        try {
          setBatch(await gateway.getBatch(batch.id));
        } catch {
          /* message above stands */
        }
      } else {
        setEdit({ lineId: edit.lineId, value: edit.value, saving: false, error: failure.message });
      }
    }
  }

  async function confirmRemove() {
    if (!batch || dialog?.kind !== 'remove') return;
    const ids = dialog.lineIds;
    const ok = await act(async () => {
      setBatch(await gateway.removeLines(batch.id, ids, batch.version));
    }, 'The employee couldn’t be removed from this batch. Try again.');
    if (ok) {
      setSelected(new Set());
      setDialog({ kind: 'removed' });
    }
  }

  async function runReadiness() {
    if (!batch) return;
    await act(async () => {
      const next = await gateway.runReadiness(batch.id, batch.version);
      setBatch(next, 2);
      requestNotificationsRefresh(); // a blocked batch can raise a notification

    }, 'The readiness check couldn’t be run. Try again.');
  }

  function continueFromReadiness() {
    if (!batch?.readiness || batch.readiness.isStale || !batch.readiness.canContinue) return;
    const warnings = batch.readiness.warnings;
    if (warnings.length === 0) {
      setActionError(null);
      setView((v) => (v ? { ...v, step: 3 } : v));
      return;
    }
    const ack = batch.warningAcknowledgement;
    const covered = ack?.acknowledged && warnings.every((w) => ack.warningIds.includes(w.id));
    if (covered) {
      setView((v) => (v ? { ...v, step: 3 } : v));
      return;
    }
    setActionError(null);
    setDialog({ kind: 'ack' });
  }

  async function acknowledge() {
    if (!batch?.readiness) return;
    const ids = batch.readiness.warnings.map((w) => w.id);
    const ok = await act(async () => {
      setBatch(await gateway.acknowledgeWarnings(batch.id, ids, batch.version), 3);
    }, 'The acknowledgement couldn’t be saved. Try again.');
    if (ok) setDialog(null);
  }

  async function saveDraft() {
    if (!batch) return;
    const ok = await act(async () => {
      setBatch(await gateway.saveDraft(batch.id, step === 4 ? 3 : step, batch.version));
    }, 'The draft couldn’t be saved. Try again.');
    if (ok) setDialog({ kind: 'draftSaved' });
  }

  async function discardDraft() {
    if (!batch) return;
    const ok = await act(async () => {
      await gateway.discard(batch.id, batch.version);
    }, 'The draft couldn’t be discarded. Try again.');
    if (ok) {
      setDialog(null);
      setSelected(new Set());
      setConfirmedVersion(null);
      setNotice('Draft discarded. The saved changes were deleted and a fresh batch was loaded from the approved payroll run.');
      setBatchNonce((n) => n + 1);
    }
  }

  async function approve() {
    if (!batch || !confirmed) return;
    if (approveKey.current?.version !== batch.version) approveKey.current = { version: batch.version, key: newKey() };
    const idempotencyKey = approveKey.current.key;
    await act(async () => {
      setBatch(await gateway.approve(batch.id, batch.version, idempotencyKey), 4);
      requestNotificationsRefresh(); // approval notifies the people who can see payment batches
    }, 'The batch couldn’t be approved. Try again.');
  }

  function backToPayEmployees() {
    // The approved run no longer needs a batch: return to an empty selection so the admin can pick another pay day.
    selectRun(null);
  }

  // ---- derived: why Approve can't be used yet -----------------------------------------------------------------------------------------------
  let approveBlockedReason: string | null = null;
  if (batch) {
    const r = batch.readiness;
    if (!r || r.isStale) approveBlockedReason = 'The readiness check is out of date. Run it again before approving.';
    else if (r.blockers.length > 0) approveBlockedReason = 'Blocking issues remain. Resolve them and run the readiness check again.';
    else if (r.warnings.length > 0 && !(batch.warningAcknowledgement?.acknowledged && r.warnings.every((w) => batch.warningAcknowledgement?.warningIds.includes(w.id)))) {
      approveBlockedReason = 'The warnings from the readiness check have not been acknowledged.';
    } else if (batch.totals.employeesIncluded === 0) approveBlockedReason = 'There are no employees in this batch.';
  }

  // ---- render -----------------------------------------------------------------------------------------------------------------------------------
  const showTabs = step === 1 || batch === null;
  const preview = batch?.isPreview ?? true;

  return (
    <div className="wc" data-testid="write-check-flow">
      {preview && <TrialBanner />}
      <h2 className="wc-title">Pay Employees</h2>

      <section className="card card--wide wc-card" aria-label="Write Check payment batch">
        <Stepper current={step} />

        {notice && <p className="wc-notice" role="status" data-testid="flow-notice">{notice}</p>}
        {actionError && <p className="error wc-action-error" role="alert" data-testid="flow-error">{actionError}</p>}

        {(step === 1 || batch === null) && (
          <>
            {runsState.kind === 'loaded' && runsState.runs.length > 0 && (
              <FilterBar runs={runsState.runs} runId={runId} disabled={busy} onSelectRun={selectRun} />
            )}
            {showTabs && tabs}
          </>
        )}

        {runsState.kind === 'loading' && <p className="wc-muted" role="status">Loading approved payroll runs…</p>}
        {runsState.kind === 'error' && (
          <div role="alert">
            <p className="error">{runsState.message}</p>
            <button type="button" className="wc-btn wc-btn--outline" onClick={() => setRunsNonce((n) => n + 1)}>Try again</button>
          </div>
        )}
        {runsState.kind === 'loaded' && runsState.runs.length === 0 && (
          <p className="wc-empty" role="status" data-testid="no-runs">
            There is no approved payroll run to pay by check yet. Approve a Custom Pay Run first, then return here.
          </p>
        )}
        {runsState.kind === 'loaded' && runsState.runs.length > 0 && runId === null && (
          <p className="wc-empty" role="status" data-testid="no-run-selected">Select a pay day to see the employees paid by check.</p>
        )}

        {runId !== null && loading && <p className="wc-muted" role="status">Loading the payment batch…</p>}
        {runId !== null && !loading && view?.error && (
          <div role="alert">
            <p className="error">{view.error}</p>
            <button type="button" className="wc-btn wc-btn--outline" onClick={() => setBatchNonce((n) => n + 1)}>Try again</button>
          </div>
        )}

        {batch && step === 1 && (
          <>
            <CheckDeliveryNote />
            <SummaryBar totals={batch.totals} />
            <CheckTable
              batch={batch}
              selectedIds={selected}
              edit={edit}
              disabled={busy}
              onToggle={toggle}
              onToggleAll={toggleAll}
              onStartEdit={startEdit}
              onChangeEdit={(value) => setEdit((e) => (e ? { ...e, value, error: null } : e))}
              onCommitEdit={() => void commitEdit()}
              onCancelEdit={() => setEdit(null)}
              onRemoveRow={(line) => setDialog({ kind: 'remove', lineIds: [line.id], label: line.employeeName })}
            />
            <p className="wc-muted wc-edit-hint">
              To edit earnings or payment amounts, go back to the{' '}
              <button type="button" className="wc-link" onClick={() => navigate('/payroll-processing')}>Custom Pay Run</button>.
            </p>
            <div className="wc-footer wc-footer--review">
              <button type="button" className="wc-btn wc-btn--outline" onClick={() => navigate('/payroll-processing')}>Back to Custom Pay Run</button>
              <div className="wc-footer__actions">
                {selectedIncluded.length > 0 && (
                  <button
                    type="button"
                    className="wc-btn wc-btn--danger-outline"
                    disabled={busy}
                    onClick={() =>
                      setDialog({
                        kind: 'remove',
                        lineIds: selectedIncluded.map((l) => l.id),
                        label: selectedIncluded.length === 1 ? (selectedIncluded[0]?.employeeName ?? 'this employee') : plural(selectedIncluded.length, 'employee', 'employees'),
                      })
                    }
                  >
                    Remove from Batch ({selectedIncluded.length})
                  </button>
                )}
                <button type="button" className="wc-btn wc-btn--primary" disabled={busy || lines.length === 0} onClick={() => void runReadiness()}>
                  Run Readiness Check
                </button>
              </div>
            </div>
          </>
        )}

        {batch && step === 2 && (
          <ReadinessStep
            batch={batch}
            busy={busy}
            onBack={() => setView((v) => (v ? { ...v, step: 1 } : v))}
            onRerun={() => void runReadiness()}
            onContinue={continueFromReadiness}
          />
        )}

        {batch && step === 3 && (
          <ReviewStep
            batch={batch}
            confirmed={confirmed}
            onConfirmedChange={(v) => setConfirmedVersion(v ? batch.version : null)}
            blockedReason={approveBlockedReason}
            approveAvailable={sandboxTransmissionActionsEnabled}
            busy={busy}
            onBack={() => setView((v) => (v ? { ...v, step: 2 } : v))}
            onDiscard={() => setDialog({ kind: 'discard' })}
            onSaveDraft={() => void saveDraft()}
            onApprove={() => void approve()}
            onRecheck={() => setView((v) => (v ? { ...v, step: 2 } : v))}
          />
        )}

        {batch && step === 4 && <ApprovedStep batch={batch} onBack={backToPayEmployees} />}
      </section>

      {/* ---- dialogs ---- */}
      {dialog?.kind === 'remove' && (
        <ConfirmCard
          title="Remove from this batch?"
          body={`Remove ${dialog.label} from this payment batch? Their employee record and payroll history are not changed, and the batch totals are recalculated.`}
          cancelLabel="No, Keep It"
          confirmLabel="Remove"
          confirmTone="danger"
          busy={busy}
          error={actionError}
          onCancel={() => {
            setActionError(null);
            setDialog(null);
          }}
          onConfirm={() => void confirmRemove()}
        />
      )}
      {dialog?.kind === 'removed' && (
        <ResultDialog
          title="Removed from the Batch"
          body="The employee was removed from this payment batch only. The batch totals were updated, and you can continue with the remaining employees."
          buttonLabel="Back to Pay Employees"
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'ack' && batch?.readiness && (
        <AckDialog
          warnings={batch.readiness.warnings}
          busy={busy}
          error={actionError}
          onCancel={() => {
            setActionError(null);
            setDialog(null);
          }}
          onConfirm={() => void acknowledge()}
        />
      )}
      {dialog?.kind === 'draftSaved' && batch && (
        <DraftDialog
          batch={batch}
          variant="saved"
          busy={busy}
          onBackToDashboard={() => navigate('/dashboards/admin-dashboard')}
          onDiscard={() => setDialog({ kind: 'discard' })}
          onContinue={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'discard' && (
        <ConfirmCard
          title="Discard Draft?"
          body="Are you sure you want to delete this draft? This action cannot be undone."
          cancelLabel="No, Keep Editing"
          confirmLabel="Yes, Delete Draft"
          confirmTone="danger"
          busy={busy}
          error={actionError}
          onCancel={() => {
            setActionError(null);
            setDialog(null);
          }}
          onConfirm={() => void discardDraft()}
        />
      )}
      {view?.existingDraft && batch && dialog === null && !loading && step === 1 && (
        <DraftDialog
          batch={batch}
          variant="existing"
          busy={busy}
          onBackToDashboard={() => navigate('/dashboards/admin-dashboard')}
          onDiscard={() => setDialog({ kind: 'discard' })}
          onContinue={() => {
            setView((v) => (v ? { ...v, existingDraft: false, step: asStep(batch.currentStep) } : v));
          }}
        />
      )}
    </div>
  );
}
